import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UpdateFormValue } from '@ngxs/form-plugin';
import { Immutable } from 'immer';
import { ToastrService } from 'ngx-toastr';
import { NavigateService } from '../../shared/services/navigate.service';
import { NgxsStateFacade } from '../../shared/store/ngxs-state-facade';
import { TvhPaymentConfig } from '../models/payment-config';
import { PaymentConfigurationApiService } from '../services/payment-configuration-api.service';

export interface PaymentConfigurationStateModel {
  configForm: {
    model: {
      minimalPaymentAmount: number;
      benefitBaseAmount: number;
      benefitAmount: number;
      financialYearStartDay: number;
      financialYearStartMonth: number;
    };
    dirty: boolean;
    status: string;
    errors: { [key: string]: unknown };
  };
  exampleMultiples: Array<number>;
}

export class UpdateConfiguration {
  static readonly type = '[PaymentConfig] Update Configuration';
  constructor(public config: TvhPaymentConfig) {}
}

export class Cancel {
  static readonly type = '[PaymentConfig] Cancel';
}

@State<PaymentConfigurationStateModel>({
  name: 'paymentConfig',
  defaults: {
    configForm: {
      model: {
        minimalPaymentAmount: 0,
        benefitBaseAmount: 0,
        benefitAmount: 0,
        financialYearStartDay: 1,
        financialYearStartMonth: 1,
      },
      dirty: false,
      status: '',
      errors: {},
    },
    exampleMultiples: [1, 2, 3, 4, 5],
  },
})
@Injectable()
export class PaymentConfigurationState extends NgxsStateFacade<PaymentConfigurationStateModel> {
  constructor(
    store: Store,
    private readonly navigate: NavigateService,
    private readonly paymentConfig: PaymentConfigurationApiService,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
  ) {
    super(store, 'paymentConfig');
  }

  @Selector()
  static bonusSystemActive(state: PaymentConfigurationStateModel): boolean {
    return !!state.configForm.model.benefitBaseAmount && !!state.configForm.model.benefitAmount;
  }

  @Selector()
  static bonusBaseAmount(state: PaymentConfigurationStateModel): number {
    return state.configForm.model.benefitBaseAmount;
  }

  @Selector()
  static bonusAmount(state: PaymentConfigurationStateModel): number {
    return state.configForm.model.benefitAmount;
  }

  @Selector()
  static minimumAmount(state: PaymentConfigurationStateModel): number {
    return state.configForm.model.minimalPaymentAmount;
  }

  @Selector()
  static exampleMultiples(state: PaymentConfigurationStateModel): Immutable<Array<number>> {
    return state.exampleMultiples;
  }

  // --- Public API ---

  get bonusSystemActive(): boolean {
    return this.store.selectSnapshot(PaymentConfigurationState.bonusSystemActive);
  }

  get bonusBaseAmount(): number {
    return this.store.selectSnapshot(PaymentConfigurationState.bonusBaseAmount);
  }

  get bonusAmount(): number {
    return this.store.selectSnapshot(PaymentConfigurationState.bonusAmount);
  }

  get minimumAmount(): number {
    return this.store.selectSnapshot(PaymentConfigurationState.minimumAmount);
  }

  get exampleMultiples(): Immutable<Array<number>> {
    return this.store.selectSnapshot(PaymentConfigurationState.exampleMultiples);
  }

  ngxsAfterBootstrap(): void {
    this.getDataFromBackend();
  }

  updateConfiguration(config: TvhPaymentConfig): void {
    this.store.dispatch(new UpdateConfiguration(config));
  }

  cancel(): void {
    this.store.dispatch(new Cancel());
  }

  // --- Actions ---

  @Action(UpdateConfiguration)
  updateConfigurationHandler(ctx: StateContext<PaymentConfigurationStateModel>, action: UpdateConfiguration) {
    return this.paymentConfig.updatePaymentConfig(action.config).subscribe(
      (updated) => {
        this.updateForm(updated);
        this.toastr.success(this.translate.instant('ADMIN.CONFIGURATION.MSG.SUCCESS'));
        this.navigate.toManagement();
      },
      () => {
        this.toastr.error(this.translate.instant('ADMIN.CONFIGURATION.MSG.FAILURE'));
      },
    );
  }

  @Action(Cancel)
  cancelHandler() {
    this.navigate.toManagement();
    this.getDataFromBackend();
  }

  private getDataFromBackend(): void {
    this.paymentConfig.getPaymentConfig().subscribe((res) => {
      this.updateForm(res);
    });
  }

  private updateForm(config: TvhPaymentConfig): void {
    this.store.dispatch(
      new UpdateFormValue({
        value: {
          minimalPaymentAmount: config.minimalPaymentAmount ?? 0,
          benefitBaseAmount: config.benefitBaseAmount ?? 0,
          benefitAmount: config.benefitAmount ?? 0,
          financialYearStartDay: config.financialYearStartDay ?? 1,
          financialYearStartMonth: config.financialYearStartMonth ?? 1,
        },
        path: 'paymentConfig.configForm',
      }),
    );
  }
}
